import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  CardActions,
  Collapse,
  TextField,
  Divider,
  IconButton,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

const UpsellCard = ({ item, onAddToCart }) => {
  const [expanded, setExpanded] = useState(false);
  const [customizations, setCustomizations] = useState({});

  if (!item) return null;

  const {
    name,
    displayName,
    image,
    unitPrice,
    originalPrice,
    notes,
    customizationFields = [],
  } = item;

  const hasCustomization = customizationFields.length > 0;
  const hasDiscount = originalPrice && originalPrice > unitPrice;

  const handleAdd = () => {
    if (hasCustomization && !expanded) {
      setExpanded(true);
      return;
    }

    onAddToCart?.(item, customizations);

    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: displayName || name,
        content_ids: [item._id],
        content_type: "product",
        value: unitPrice,
        currency: "CAD",
      });
    }

    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "CAD",
        value: unitPrice,
        items: [
          {
            item_id: item._id,
            item_name: displayName || name,
            price: unitPrice,
            quantity: 1,
          },
        ],
      });
    }

    setExpanded(false);
    setCustomizations({});
  };

  return (
    <Card
      sx={{
        width: 200,
        borderRadius: 2,
        boxShadow: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 250,
        mr: 2,
      }}>
      <CardMedia
        component="img"
        image={image}
        alt={displayName || name}
        sx={{
          height: 150,
          objectFit: "cover",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      />
      <CardContent sx={{ px: 1.5, pt: 1.5, pb: 0, flexGrow: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
          {displayName || name}
        </Typography>

        <Box sx={{ mt: 0.5, display: "flex", alignItems: "baseline", gap: 1 }}>
          {hasDiscount ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: "line-through",
                  color: "text.secondary",
                  fontWeight: 400,
                }}>
                ${originalPrice.toFixed(2)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "green", fontWeight: 600 }}>
                ${unitPrice.toFixed(2)}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" fontWeight={500}>
              ${unitPrice.toFixed(2)}
            </Typography>
          )}
        </Box>
      </CardContent>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          {customizationFields?.map((field) => {
            if (field.type === "text") {
              const value = customizations[field.key] || "";
              const maxLength = field.maxLength || 25;
              const reachedMax = value.length >= maxLength;

              return (
                <Box key={field.key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label || "Message"}
                    value={value}
                    onChange={(e) =>
                      setCustomizations((prev) => ({
                        ...prev,
                        [field.key]: e.target.value.slice(0, maxLength),
                      }))
                    }
                    helperText={`${value.length} / ${maxLength} chars allowed`}
                    FormHelperTextProps={{
                      sx: {
                        color: reachedMax ? "error.main" : "text.secondary",
                        fontSize: "0.75rem",
                        mt: 0.5,
                      },
                    }}
                    sx={{
                      mt: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                    InputProps={{
                      endAdornment: value.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            setCustomizations((prev) => ({
                              ...prev,
                              [field.key]: "",
                            }))
                          }
                          sx={{ color: "#999" }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              );
            }
            return null;
          })}
        </Box>
      </Collapse>

      <CardActions sx={{ px: 1.5, pb: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddShoppingCartIcon />}
          onClick={handleAdd}
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "8px",
          }}>
          {expanded ? "Confirm" : "Add"}
        </Button>
      </CardActions>

      {notes && (
        <>
          <Divider sx={{ mx: 1.5, mb: 1 }} />
          <Box
            sx={{
              px: 1.5,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ mt: "2px", color: "text.secondary" }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.72rem" }}>
              {notes}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
};

export default UpsellCard;
